//@ts-check
import _ from 'lodash';
import Joi from 'joi-browser';
import { USER_ROLES, STATUS_CODE } from '../../utilities/constants';
import { LwUser, LwUsers, EditName } from './Base';
import LwGroupApi from '../LwGroupApi';
import { HttpError } from './Error';

/*
LINE WOKRSモーダル内のグループ関係のみで取り扱うクラスや関数。
「_」付きの変数や関数を直接取得・変更してはならない。
(プライベート宣言はESLint非対応のため未使用)
*/

class AppendableUser extends LwUser {
  /**
   * LINE WROKSユーザーの情報を格納するクラス
   * @param {any} parameter
   * 詳細はLwUserクラスを参照
   **/

  _LW_ACCEPT_FULL_NAME_SIZE = 80;
  _ERROR_MESSAGE = {
    unregistered_email_address:
      'メールアドレスが登録されていない職長はLINE WORKSに登録できません。', //SSOなし・職長限定
    over_80_full_name:
      '氏名が80文字を超えているユーザーはLINE WORKSに登録できません。',
    has_fullwidth_space:
      '氏名に全角スペースが含まれるユーザーはLINE WORKSに登録できません。',
  };
  /**
   * 追加可能なユーザーのエラーメッセージリストを取得する
   * @param {boolean} isSso - SSOを利用しているか否か
   **/

  getErrorMessageList(isSso) {
    /** @type {string[]} */
    const message_list = [];
    if (
      !isSso &&
      !this._parameter.is_email &&
      this._parameter.role_code === USER_ROLES.foreman.id
    ) {
      message_list.push(this._ERROR_MESSAGE.unregistered_email_address);
    }
    // 性と名の間に半角の空白が入るので、それを除いて80文字以上
    if (
      !(
        this._LW_ACCEPT_FULL_NAME_SIZE >=
        [...this._parameter.fullname].length - 1
      )
    ) {
      message_list.push(this._ERROR_MESSAGE.over_80_full_name);
    }
    const reg = new RegExp(/^(?=.*\u3000).*$/);
    if (reg.test(this._parameter.fullname)) {
      message_list.push(this._ERROR_MESSAGE.has_fullwidth_space);
    }
    return Object.freeze(message_list);
  }
}

class AppendableUsers extends LwUsers {
  /**
   * 追加可能なユーザーリストを格納するコレクションクラス
   * @param {AppendableUser[]} appendableUserList - 追加可能なユーザーのリスト
   **/
  constructor(appendableUserList) {
    super(appendableUserList);
  }

  /**
   * LINE WORKS グループ追加可能ユーザー取得APIからこのクラスを作る。
   * @param {number} fieldId 現場ID
   * @param {import("./SearchCondition").SearchCondition} searchCondition 検索条件
   * @return {Promise<{appendableUsers: AppendableUsers, isSso: boolean}>} 取得出来たユーザー
   */
  static async createByApiData(fieldId, searchCondition) {
    const { data, status } = await LwGroupApi.appendable_users(
      fieldId,
      searchCondition.getAPIParamertes()
    );
    if (status === STATUS_CODE.badRequest) {
      // 型情報がおかしいが、正常にエラーを呼び出せるので無視する
      // @ts-ignore
      throw new HttpError(_.get(data, 'message', '') || '');
    }
    const originAppendableUsers =
      _.get(data, 'entities.appendable_users', []) || [];
    const appendableUsersList = _.map(originAppendableUsers, function(
      userData
    ) {
      return new AppendableUser(userData);
    });
    const appendableUsers = new AppendableUsers(appendableUsersList);
    const isSso = _.get(data, 'entities.is_sso', false) || false;
    return { appendableUsers, isSso };
  }
  /**
   * @param {LwUser[]} otherLwUserList 追加しようとしている他のユーザークラスのリスト
   * @return {AppendableUsers} ユーザ追加後のLINE WROKSユーザークラス
   **/
  addOtherUsers(otherLwUserList) {
    return super._addOtherUsers(
      otherLwUserList,
      AppendableUser,
      AppendableUsers
    );
  }

  /**
   * @param {number[]} lwUserIds 削除しようとしているユーザーのID配列
   * @return {AppendableUsers} ユーザ削除後のLINE WROKSユーザークラス
   **/
  removeUsers(lwUserIds) {
    return super._removeUsers(lwUserIds, AppendableUsers);
  }
}

class AssignUser extends LwUser {
  /**
   * グループに追加しようとしているユーザーの情報を格納するクラス
   * @param {any} parameter
   * 詳細はLwUserクラスを参照
   **/
}

class AssignUsers extends LwUsers {
  /**
   * グループに追加しようとしているユーザーリストを格納するコレクションクラス
   * @param {AssignUser[]} assignUserList - 追加しようとしているユーザーのリスト
   **/
  constructor(assignUserList) {
    super(assignUserList);
  }

  /**
   * LINE WORKS グループ詳細取得APIから本クラスを作る。
   * @param {number} fieldId 現場ID
   * @param {number} lwGroupId グループID
   * @param {import("./SearchCondition").SearchCondition} searchCondition 検索条件
   * @return {Promise<AssignUsers>} 取得出来たユーザー
   */
  static async createByApiData(fieldId, lwGroupId, searchCondition) {
    if (!lwGroupId) {
      return new AssignUsers([]);
    }
    const { data, status } = await LwGroupApi.detail(
      fieldId,
      lwGroupId,
      searchCondition.getAPIParamertes()
    );
    if (status === STATUS_CODE.badRequest) {
      // 型情報がおかしいが、正常にエラーを呼び出せるので無視する
      // @ts-ignore
      throw new HttpError(_.get(data, 'message', '') || '');
    }
    const originAssignUsers =
      _.get(data, 'entities.lw_group.members', []) || [];
    const assignUsers = _.map(originAssignUsers, function(userData) {
      return new AssignUser(userData);
    });
    return new AssignUsers(assignUsers);
  }

  /**
   * @param {LwUser[]} otherLwUserList 追加しようとしている他のユーザークラスのリスト
   * @return {AssignUsers} ユーザ追加後のLINE WROKSユーザークラス
   **/
  addOtherUsers(otherLwUserList) {
    return super._addOtherUsers(otherLwUserList, AssignUser, AssignUsers);
  }

  /**
   * @param {number[]} lwUserIds 削除しようとしているユーザーのID配列
   * @return {AssignUsers} ユーザ削除後のLINE WROKSユーザークラス
   **/
  removeUsers(lwUserIds) {
    return super._removeUsers(lwUserIds, AssignUsers);
  }
}

const groupNameSchema = Joi.object().keys({
  name: Joi.string()
    .max(100)
    .required(),
});

class GroupName extends EditName {
  /**
   * グループ名を格納するクラス
   * @param {string} groupName - グループ名
   **/
  constructor(groupName) {
    super(groupName, groupNameSchema);
  }

  /**
   * LINE WORKS グループ取得APIからこのクラスを作る。
   * @param {number} fieldId 現場ID
   * @return {Promise<GroupName>} 取得出来たグループ名
   */
  static async createByApiData(fieldId) {
    const { data } = await LwGroupApi.list(fieldId);
    /** @type {string} **/
    const groupName = _.get(data, 'entities.lw_group.name', '') || '';
    return new GroupName(groupName);
  }

  /**
   * @param {string} groupName - グループ名
   * @return {GroupName}
   **/
  setValue(groupName) {
    return super._setValue(groupName, GroupName, groupNameSchema);
  }
}

export { AppendableUser, AppendableUsers, AssignUser, AssignUsers, GroupName };



// WEBPACK FOOTER //
// ./src/services/lwUser/Group.js